import { useMemo, useState } from "react";
import { ArrowUpDown } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Structure } from "@/types/designs";
import { DesignToggleField } from "./DesignTableCell";

type OpticalPortsTableProps = {
  structure: Structure;
  isEditing?: boolean;
};

type OpticalPortSortKey = "name" | "orientation" | "cellPort" | "tag" | "x" | "y";
type SortDirection = "asc" | "desc";

const getComparableValue = (value: string | number | undefined) => {
  if (typeof value === "number") return value;
  return String(value ?? "")
    .trim()
    .toLowerCase();
};

export function OpticalPortsTable({ structure, isEditing = false }: OpticalPortsTableProps) {
  const [sortState, setSortState] = useState<{
    key: OpticalPortSortKey;
    direction: SortDirection;
  } | null>(null);

  const sortedRows = useMemo(() => {
    const rows = structure.opticalPorts ?? [];
    if (!sortState) return rows;

    const { key, direction } = sortState;
    const multiplier = direction === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
      const leftValue = getComparableValue((a as Record<string, string | number | undefined>)[key]);
      const rightValue = getComparableValue(
        (b as Record<string, string | number | undefined>)[key],
      );

      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return (leftValue - rightValue) * multiplier;
      }

      return String(leftValue).localeCompare(String(rightValue)) * multiplier;
    });
  }, [structure.opticalPorts, sortState]);

  const handleSort = (key: OpticalPortSortKey) => {
    setSortState((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }

      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key: OpticalPortSortKey) => {
    if (sortState?.key !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />;
    }

    return <span className="text-xs font-medium">{sortState.direction === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <div className="m-0">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[160px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("name")}
              >
                Name {renderSortIcon("name")}
              </button>
            </TableHead>
            <TableHead className="w-[160px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("orientation")}
              >
                Orientation {renderSortIcon("orientation")}
              </button>
            </TableHead>
            <TableHead className="w-[160px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("cellPort")}
              >
                Cell Port {renderSortIcon("cellPort")}
              </button>
            </TableHead>
            <TableHead className="w-[180px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("tag")}
              >
                Tag {renderSortIcon("tag")}
              </button>
            </TableHead>
            <TableHead className="w-[100px] text-right">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("x")}
              >
                X {renderSortIcon("x")}
              </button>
            </TableHead>
            <TableHead className="w-[100px] text-right">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("y")}
              >
                Y {renderSortIcon("y")}
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length > 0 ? (
            sortedRows.map((port) => (
              <TableRow key={port.id}>
                <TableCell className="w-[160px]">
                  <DesignToggleField value={port.name} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[160px]">
                  <DesignToggleField value={port.orientation} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[160px]">
                  <DesignToggleField value={port.cellPort} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[180px]">
                  <DesignToggleField value={port.tag} isEditing={isEditing} />
                </TableCell>
                <TableCell className="w-[100px] text-right">
                  <DesignToggleField
                    value={port.x}
                    isEditing={isEditing}
                    inputClassName="h-8 w-full text-right"
                  />
                </TableCell>
                <TableCell className="w-[100px] text-right">
                  <DesignToggleField
                    value={port.y}
                    isEditing={isEditing}
                    inputClassName="h-8 w-full text-right"
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                No optical ports available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
